import { ComponentProps, useCallback } from 'react';
import { CatalogEntry, LearningPath, LiveEvent } from 'types';
import { OfferingCard } from '@ole-ui/ole-ui-components';
import { useTranslation } from 'react-i18next';

import { PremiumSessionUserFacingStatus } from 'types/premiumSession';
import usePremiumSessionAggregateInfo from 'hooks/usePremiumSessionAggregateInfo';
import useUserTimezone from 'hooks/useUserTimezone';
import { CourseLvcUserFacingStatus } from 'views/Catalog/useCourseLiveClassInfo';
import { LiveVirtualClassInfo, OfferingTypes } from '../../utils/types';
import useOfferingInfo from './useOfferingInfo';
import offeringTypeRegistry from '../../configs';

type UseBodyRowCustomProps = {
  attendedSessionDate?: string;
  entry: CatalogEntry;
  learningPath?: LearningPath;
  liveEventInfo?: LiveEvent;
  liveVirtualClassInfo?: LiveVirtualClassInfo;
  lvcStatus?: CourseLvcUserFacingStatus;
  offeringType: OfferingTypes;
  premiumSessionStatus: PremiumSessionUserFacingStatus;
  schedulledSessionDate?: string;
  sessionProgress?: {
    hasSessionProgress: boolean;
    sessionsStatusList: ReturnType<
      typeof usePremiumSessionAggregateInfo
    >['sessionsStatusList'];
  };
};

const useBodyRowCustom = ({
  attendedSessionDate,
  entry,
  learningPath,
  liveEventInfo,
  liveVirtualClassInfo,
  lvcStatus,
  offeringType,
  premiumSessionStatus,
  schedulledSessionDate,
  sessionProgress,
}: UseBodyRowCustomProps) => {
  const {
    examExperienceQuota,
    examStatusInfo,
    offeringProducts,
    offeringTopics,
  } = useOfferingInfo(entry);
  const { t } = useTranslation();
  const { userTimezone } = useUserTimezone();

  const buildCustomRows = useCallback<
    () => ComponentProps<typeof OfferingCard.BodyRow>[]
  >(
    () =>
      offeringTypeRegistry[offeringType]?.buildCustomRows?.({
        attendedSessionDate,
        examExperienceQuota,
        examStatusInfo,
        learningPath,
        liveEventInfo,
        liveVirtualClassInfo,
        lvcStatus,
        offeringProducts,
        offeringTopics,
        premiumSessionStatus,
        schedulledSessionDate,
        sessionProgress,
        t,
        userTimezone,
      }) ?? [],
    [
      attendedSessionDate,
      examExperienceQuota,
      examStatusInfo,
      learningPath,
      liveEventInfo,
      liveVirtualClassInfo,
      lvcStatus,
      offeringProducts,
      offeringTopics,
      offeringType,
      premiumSessionStatus,
      schedulledSessionDate,
      sessionProgress,
      t,
      userTimezone,
    ],
  );

  return { buildCustomRows };
};

export default useBodyRowCustom;
